import type { SnippetBodyFormat } from '../../types/snippetBodyFormat.ts';
import type { SnippetProduct } from '../../types/snippetProduct.ts';
import type { SnippetScope } from '../../types/snippetScope.ts';
import type { SnippetStatus } from '../../types/snippetStatus.ts';

export class SnippetError extends Error {
	statusCode?: number;

	constructor(message: string) {
		super(`Snippet error: ${message}`);
	}
}

const BODY_TOO_LARGE_PATTERN = /body must be \d+\s*KB or less/i;

/**
 * Returns true when the error is a 400 SnippetError caused by the template body exceeding the size limit.
 */
export const isBodyTooLargeError = (err: unknown): boolean =>
	err instanceof SnippetError && err.statusCode === 400 && BODY_TOO_LARGE_PATTERN.test(err.message);

export type SnippetData = {
	resourceId: string; // ARI of the Snippet
	snippetId: string;
	name: string; // User defined name
	description?: string; // User defined name
	body: string; // String of the type defined in `bodyFormat` (example: "ADF")
	bodyFormat: SnippetBodyFormat;
	product: SnippetProduct;
	label?: string;
	isFavorite: boolean;
	status: SnippetStatus;
	scope: SnippetScope;
	/** When true, template is shared with the site. Returned by BE for USER and SITE scope; may be omitted for GLOBAL */
	isShared?: boolean;
	createdBy: string;
	createdAt: string; // String of integer timestamp
	updatedAt: string; // String of integer timestamp
	insertCount?: number;
	shareId?: string;
};

/** Payload for creating or updating a snippet */
export type CreateSnippetPayload = {
	name: string;
	body: string;
	bodyFormat: SnippetBodyFormat;
	product: SnippetProduct;
	description?: string;
};

export type UpdateSnippetPayload = {
	name: string;
	body: string;
	description?: string;
};

export type SnippetFavoriteData = {
	userId: string;
	snippetIds: string[]; // Array of snippet ARIs
};
